import { AfterViewInit, Component, ElementRef, Input, NgZone, OnChanges, PLATFORM_ID, ViewChild, inject } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { environment } from '../../../environments/environment';
export interface MapMarker {
  lat:      number;
  lng:      number;
  title:    string;
  type:     string;
  building: string;
}

const TYPE_COLORS: Record<string, string> = {
  'Lockdown':            '#ef4444',
  'Fire':                '#f97316',
  'Police':              '#3b82f6',
  'Medical':             '#22c55e',
  'Administrative':      '#a855f7',
  'Test Lockdown':       '#9ca3af',
  'Test Fire':           '#9ca3af',
  'Test Police':         '#9ca3af',
  'Test Medical':        '#9ca3af',
  'Test Administrative': '#9ca3af'
};

// SVG icon paths keyed by base alert type (without "Test " prefix)
const TYPE_ICONS: Record<string, string> = {
  'Lockdown':       'M12 1 3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z',
  'Fire':           'M12 23C9.38 23 5 19.78 5 14.5c0-2.4 1.25-5.25 3.27-7.36C9.85 5.55 10.5 3.08 10.5 1.5c0-.28.22-.5.5-.5s.5.22.5.5c0 1.5.65 3.97 2.23 5.64C15.63 9.18 19 11.5 19 14.5 19 19.78 14.62 23 12 23z',
  'Police':         'M12 2l2.4 4.87 5.38.78-3.89 3.79.92 5.36L12 14.27l-4.81 2.53.92-5.36L4.22 7.65l5.38-.78z',
  'Medical':        'M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-2 10h-4v4h-2v-4H7v-2h4V7h2v4h4v2z',
  'Administrative': 'M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z',
};

function makeIconMarker(type: string, color: string): HTMLElement {
  const baseType = type.startsWith('Test ') ? type.slice(5) : type;
  const path = TYPE_ICONS[baseType] ?? TYPE_ICONS['Lockdown'];

  const el = document.createElement('div');
  el.style.cssText = [
    'display:flex', 'align-items:center', 'justify-content:center',
    'width:36px', 'height:36px', 'border-radius:50%',
    `background:${color}`,
    'box-shadow:0 2px 8px rgba(0,0,0,.35)',
    'border:2.5px solid rgba(255,255,255,.85)',
    'cursor:pointer',
  ].join(';');

  el.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="${path}"/></svg>`;
  return el;
}


const DEFAULT_CENTER = { lat: 37.3382, lng: -121.8865 };


let scriptPromise: Promise<void> | null = null;

function loadMapsScript(apiKey: string): Promise<void> {
  if (scriptPromise) return scriptPromise;
  if (typeof google !== 'undefined' && google.maps?.Map) {
    scriptPromise = Promise.resolve();
    return scriptPromise;
  }
  scriptPromise = new Promise<void>(resolve => {
    (window as any)['__mapsInitialized'] = resolve;
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&loading=async&libraries=marker&callback=__mapsInitialized`;
    script.async = true;
    document.head.appendChild(script);
  });
  return scriptPromise;
}


@Component({
  selector: 'app-google-map',
  standalone: true,
  imports: [CommonModule],
  template: `<div #mapEl class="w-full h-full rounded-lg min-h-[160px]"></div>`
})
export class GoogleMapComponent implements AfterViewInit, OnChanges {
  @Input() markers: MapMarker[] = [];
  @ViewChild('mapEl', { static: true }) mapEl!: ElementRef<HTMLDivElement>;

  private readonly platformId = inject(PLATFORM_ID);
  private readonly zone       = inject(NgZone);

  private map: google.maps.Map | null = null;
  private infoWindow: google.maps.InfoWindow | null = null;
  private markerInstances: google.maps.marker.AdvancedMarkerElement[] = [];
  private mapReady = false;

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    loadMapsScript(environment.mapsApiKey).then(() => {
      this.zone.runOutsideAngular(() => {
        this.map = new google.maps.Map(this.mapEl.nativeElement, {
          center: DEFAULT_CENTER,
          zoom: 15,
          mapId: 'DEMO_MAP_ID',
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false
        });
        this.infoWindow = new google.maps.InfoWindow();
        this.mapReady = true;
        this.renderMarkers();
      });
    });
  }

  ngOnChanges(): void {
    if (this.mapReady) {
      this.zone.runOutsideAngular(() => this.renderMarkers());
    }
  }

  private renderMarkers(): void {
    if (!this.map) return;

    this.markerInstances.forEach(m => (m.map = null));
    this.markerInstances = [];

    if (!this.markers.length) return;

    const bounds = new google.maps.LatLngBounds();

    this.markers.forEach(data => {
      const position = { lat: data.lat, lng: data.lng };
      const color    = TYPE_COLORS[data.type] ?? '#6b7280';

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position,
        map:     this.map!,
        title:   data.title,
        content: makeIconMarker(data.type, color)
      });

      marker.addListener('gmp-click', () => {
        const date = new Date(data.title + (data.title.endsWith('Z') ? '' : 'Z'));
        const formatted = date.toLocaleString(undefined, {
          month: '2-digit', day: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit'
        });
        this.infoWindow!.setContent(`
          <div style="font-family:sans-serif;font-size:10px;padding:2px 1px;line-height:1.5;min-width:120px">
            <strong style="font-size:11px">${data.building}</strong><br/>
            <span style="display:inline-block;background:${color};color:#fff;border-radius:999px;padding:1px 6px;font-size:9px">${data.type}</span><br/>
            <span style="color:#6b7280;font-size:9px">&#128337; ${formatted}</span>
          </div>
        `);
        this.infoWindow!.close();
        this.infoWindow!.setPosition(position);
        this.infoWindow!.open({ map: this.map!, shouldFocus: false });
      });

      bounds.extend(position);
      this.markerInstances.push(marker);
    });

    if (this.markerInstances.length === 1) {
      this.map.setCenter(bounds.getCenter());
      this.map.setZoom(15);
    } else {
      this.map.fitBounds(bounds, 40);
    }
  }
}
